import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  CalendarIcon,
  ChartBarIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import CountUp from 'react-countup';
import { supabase } from '../lib/supabase';
import { getPlayersByTournament, getTournamentEvents } from '../lib/supabase';

interface TechnicalDashboardHomeProps {
  tournamentId: string;
}

interface TechnicalStats {
  players: number;
  activePlayers: number;
  scoredEntries: number;
  events: { total: number; upcoming: number };
  rounds: number;
}

function TechnicalDashboardHome({ tournamentId }: TechnicalDashboardHomeProps) {
  const [stats, setStats] = useState<TechnicalStats>({
    players: 0,
    activePlayers: 0,
    scoredEntries: 0,
    events: { total: 0, upcoming: 0 },
    rounds: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tournamentId) fetchStats();
  }, [tournamentId]);

  const fetchStats = async () => {
    try {
      setLoading(true);

      const [playersRes, eventsRes, roundsRes, scoresRes] = await Promise.all([
        getPlayersByTournament(tournamentId),
        getTournamentEvents(tournamentId),
        supabase.from('rounds').select('id', { count: 'exact', head: true }).eq('tournament_id', tournamentId),
        supabase.from('scores').select('player_id', { count: 'exact', head: true }).eq('tournament_id', tournamentId),
      ]);

      const now = new Date();
      const playersData = playersRes.data || [];
      const eventsData = eventsRes.data || [];

      setStats({
        players: playersData.length,
        activePlayers: playersData.filter(p => p.status?.toLowerCase() === 'active').length,
        scoredEntries: scoresRes.count || 0,
        events: {
          total: eventsData.length,
          upcoming: eventsData.filter(e => new Date(e.date) > now).length,
        },
        rounds: roundsRes.count || 0,
      });
    } catch (err) {
      console.error('Error fetching technical stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
      </div>
    );
  }

  const statCards = [
    { label: 'Players', value: stats.players, icon: UserGroupIcon, color: 'text-blue-600', bg: 'bg-blue-500/10', detail: `${stats.activePlayers} active` },
    { label: 'Score Entries', value: stats.scoredEntries, icon: ClipboardDocumentListIcon, color: 'text-emerald-600', bg: 'bg-emerald-500/10', detail: `${stats.rounds} rounds` },
    { label: 'Upcoming Events', value: stats.events.upcoming, icon: CalendarIcon, color: 'text-orange-600', bg: 'bg-orange-500/10', detail: `${stats.events.total} total events` },
  ];

  const quickLinks = [
    { label: 'Scores & Scorecards', description: 'View & manage scores', tab: 'scores', icon: ChartBarIcon },
    { label: 'Player Management', description: 'Player roster & details', tab: 'players', icon: UserGroupIcon },
    { label: 'Event Calendar', description: 'Tournament schedule', tab: 'calendar', icon: CalendarIcon },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="bg-emerald-500/10 p-2 rounded-lg">
          <ClipboardDocumentListIcon className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-golf-gray">Technical Dashboard</h2>
          <p className="text-sm text-gray-500">Scoring, players, and event operations</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {statCards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: i * 0.07 }}
            className="bg-white rounded-xl shadow-sm p-5 border border-gray-200"
          >
            <div className="flex items-center gap-3">
              <div className={`${card.bg} p-2.5 rounded-xl`}>
                <card.icon className={`w-5 h-5 ${card.color}`} />
              </div>
              <div>
                <p className="text-xs text-gray-500">{card.label}</p>
                <CountUp end={card.value} duration={1.5} className="text-xl font-bold text-golf-gray" />
                <p className="text-[11px] text-gray-400 mt-0.5">{card.detail}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-golf-gray">Quick Access</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
          {quickLinks.map(link => (
            <button
              key={link.tab}
              onClick={() => {
                const event = new CustomEvent('navigate-tab', { detail: link.tab });
                window.dispatchEvent(event);
              }}
              className="flex items-center gap-3 p-5 hover:bg-gray-50 transition-colors text-left"
            >
              <div className="bg-emerald-500/10 p-2 rounded-lg flex-shrink-0">
                <link.icon className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-golf-gray">{link.label}</p>
                <p className="text-xs text-gray-500">{link.description}</p>
              </div>
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

export default TechnicalDashboardHome;
